/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { IoMdClose } from "react-icons/io";
import ReactTooltip from "react-tooltip";
import { ICategoryType } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { customTooltip } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { getScrollParent } from "../../utils/DOMHelper";
import { default as IconBerserkerMail } from "../Icon";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import { ILinkOption } from "./AutoCompleteInterestTag";

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
`;

const InputContainer = styled.div`
  width: 100%;

  .__react_component_tooltip {
    padding: 8px !important;
    background: #c41700 !important;
    &::after {
      left: 5% !important;
    }
  }
`;

const StyledIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 12px;
`;

interface InputProps {
  active: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 42px;
  border: 1px solid #c41700;
  box-shadow: 0px 0px 6px #c417004d;

  border-radius: 5px;
  outline: none;
  font-size: 16px;
  padding: 5px;
  color: #383838;
  text-indent: 25px;
  background-color: #ffffff;
  padding-right: 15px

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  ${(props) =>
    props.active &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  &::placeholder {
    color: #979797;
  }
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
`;

interface DropViewProps {
  position: { top: number; left: number };
  width: number;
  offset: number;
  absolutePosition?: boolean;
}

const StyledAutoCompleteList = styled.div<DropViewProps>`
  position: fixed;
  background-color: #fff;
  z-index: 15;
  max-height: 260px;
  flex: 1;
  overflow: auto;
  border-radius: 4px;
  box-shadow: 0px 3px 6px #0000001a;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  width: ${(props) => props.width}px;
  ${({ absolutePosition, offset, position }) =>
    !absolutePosition &&
    css`
      right: 0;
      top: ${offset > 0 ? `auto` : `${position.top + 5}px`};
      left: ${position.left}px;
      bottom: ${offset > 0 ? `${offset + 5}px` : "auto"};
    `}

  cursor: default;
`;

const Cancel = styled.div`
  font: normal normal 600 15px;
  position: absolute;
  cursor: pointer;
  right: 6px;
  top: 6px;
  align-items: center;
  justify-content: center;
  width: 70px;
  font-weight: 600;
  height: 30px;
  display: flex;
  background-color: #fff2e5;
  border-radius: 4px;
  color: #fb972e;
  margin-right: 5px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const CreateCategory = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #ebebeb;
  background-color: #fff2e5;
  color: #fb972e;

  &:hover {
    background-color: #fb972e;
    color: #fff;

    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

const CreateCategoryTitle = styled.span`
  font-size: 16px;
  margin-left: 5px;
  font-weight: normal;
  display: flex;
  align-items: center;
  width: 100%;
`;

type IStyledAutocompleteSelectProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  tagValue: string;
  placeholder?: string;
  absolutePosition?: boolean;
  suggestions: IOption[] | undefined;
  selectedValue: string;
  handleCustomKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onEnter: (options: IOption) => void;
  onValueChange?: (value: string) => void;
  onValueSelect: (options: IOption) => void;
  viewCategories?: () => void;
  dropdownHeader?: JSX.Element;
  noResultsComponent?: JSX.Element;
  isCategoryComponent?: boolean;
  onCancel: () => void;
  linkSuggestions: ILinkOption[] | undefined;
  handleCategoryValueSelect: (categoryName: string) => void;
  createOnly?: boolean;
};

interface IItem {
  groupName?: string;
  name: string;
  icon?: string;
  suffix?: string;
  type?: ICategoryType;
  index: number;
  isActive: boolean;
}

const StyledClearIcon = styled.div<{ isCreatingTag: boolean }>`
  position: absolute;
  cursor: pointer;
  right: ${(props) => (props.isCreatingTag ? "90px" : "14px")};
  top: 10px;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;

  :hover {
    background-color: #22222217;
  }
`;

export default function InterestTagName(
  props: IStyledAutocompleteSelectProps
): JSX.Element {
  const {
    linkSuggestions,
    tagValue,
    suggestions,
    onEnter,
    selectedValue,
    absolutePosition,
    onValueSelect,
    onValueChange,
    placeholder,
    dropdownHeader,
    noResultsComponent,
    isCategoryComponent,
    onCancel,
    handleCategoryValueSelect,
    createOnly,
  } = props;
  const [currentFocus, setCurrentFocus] = useState(0);

  const modalHandler = useModal();

  const [filteredSuggestions, setFilteredSuggestions] = useState<IItem[]>();

  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);

  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const selectRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const suggestionRefMap = useRef<{
    [suggestionValue: string]: HTMLDivElement | null;
  }>({});

  const [dropviewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  const dropViewRef = useRef<HTMLDivElement | null>(null);
  const [offsetPopover, setOffsetPopover] = useState(0);

  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  function updateDropViewPosition() {
    if (!wrapperRef.current) return;

    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  useEffect(() => {
    if (!dropViewRef.current || !wrapperRef.current) return;

    const elementBoundingRect = (
      dropViewRef.current as HTMLDivElement
    ).getBoundingClientRect();

    const containerBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    let bottom = elementBoundingRect.bottom;

    if (offsetPopover !== 0)
      bottom =
        bottom + elementBoundingRect.height + containerBoundingRect.height;

    if (bottom >= 0 && bottom >= window.innerHeight) {
      setOffsetPopover(window.innerHeight - containerBoundingRect.top);
    } else {
      setOffsetPopover(0);
    }
  });

  useEffect(() => {
    setTimeout(() => {
      handleFilterSuggestion();
      setOpenDropView(true);
    }, 100);
  }, []);

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  useEffect(() => {
    if (!openDropView) {
      setCurrentFocus(0);
      return () => {};
    }

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(selectRef.current, selectRef.current);

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, openDropView]);

  function handleFilterSuggestion() {
    if (suggestions) {
      const suggestionsArray = suggestions
        .map((suggestion, index) => ({
          ...suggestion,
          groupName: suggestion.groupName,
          value: suggestion.name,
          index,
          isActive: false,
        }))
        .filter((suggestion) => {
          if (suggestion.categoryType === ICategoryType.System) return false;

          return suggestion.value
            .toLocaleLowerCase()
            .replace(/\s\s+/g, " ")
            .trim()
            .includes(
              selectedValue.toLocaleLowerCase().replace(/\s\s+/g, " ").trim()
            );
        });

      setFilteredSuggestions(suggestionsArray);
    }
    updateDropViewPosition();
  }

  useEffect(() => {
    handleFilterSuggestion();
  }, [suggestions, selectedValue]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  useEffect(() => {
    setFilteredSuggestions((prevSuggestionList) => {
      if (prevSuggestionList) {
        return prevSuggestionList.map((suggestionItem, index) => {
          if (index === currentFocus) {
            return {
              ...suggestionItem,
              isActive: true,
            };
          }
          return {
            ...suggestionItem,
            isActive: false,
          };
        });
      }
      return undefined;
    });
  }, [currentFocus]);

  function typedValidValue(): IOption | undefined {
    if (suggestions && selectedValue) {
      const tag = suggestions.find((suggestion) => {
        return suggestion.name === selectedValue;
      });
      return tag;
    }
    return undefined;
  }

  function getContainerWidth() {
    if (!containerRef.current) return 0;

    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    ).getBoundingClientRect();

    return (elementBoundingRect.width - 330) / 2;
  }

  function handleLoseFocus() {
    const category = typedValidValue();
    // The value he typed is already a value in the suggestion list
    if (category) {
      setOpenDropView(false);
      onValueSelect({ value: `${category.value}`, name: category.name });
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    console.log("key down", e.key);
    if (suggestions && filteredSuggestions) {
      const lastItem = filteredSuggestions.length - 1;
      if (e.key === "ArrowDown") {
        e.preventDefault();

        let currentIndex = 0;

        if (currentFocus === lastItem) {
          setCurrentFocus(0);
        } else {
          setCurrentFocus(currentFocus + 1);
          currentIndex = currentFocus + 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();

        let currentIndex = lastItem;
        if (currentFocus === 0) {
          setCurrentFocus(lastItem);
        } else {
          setCurrentFocus(currentFocus - 1);
          currentIndex = currentFocus - 1;
        }

        let currentSuggestionActive = filteredSuggestions[currentIndex];

        suggestionRefMap.current[currentSuggestionActive.name]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (e.key === "Enter") {
        console.log("Enter pressed");
        e.preventDefault();
        if (currentFocus > -1) {
          if (filteredSuggestions[currentFocus]) {
            const tag = suggestions[currentFocus];
            setOpenDropView(false);

            onEnter({ value: `${tag.value}`, name: tag.name });
          }
        } else {
          const tag = typedValidValue();
          if (tag) {
            setOpenDropView(false);

            onEnter({ value: `${tag.value}`, name: tag.name });
          }
        }
      }
    }
  }

  function clearInput() {
    if (!onValueChange) return;
    onValueChange("");
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setCurrentFocus(-1);
    setOpenDropView(true);

    if (onValueChange) onValueChange(value);
  }

  return (
    <Container ref={containerRef}>
      <AutoCompleteDiv ref={selectRef}>
        <InputContainer data-tip data-for="error" ref={wrapperRef}>
          {!openDropView && (
            <ReactTooltip
              id="error"
              place="top"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
              offset={{
                left: getContainerWidth() > 0 ? getContainerWidth() : 0,
              }}
              arrowColor={"#c41700"}
            >
              <TooltipText>
                {"You must choose a category to save your new tag"}
              </TooltipText>
            </ReactTooltip>
          )}

          <Input
            onClick={() => {
              handleFilterSuggestion();
              setOpenDropView(true);
            }}
            onBlur={handleLoseFocus}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            value={selectedValue}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
            autoFocus
            onFocus={() => {
              handleFilterSuggestion();
              setOpenDropView(true);
            }}
            key={`dropViewWidth ${dropViewWidth} - ${dropviewPosition.left} - ${dropviewPosition.top}`}
            active={openDropView}
          />
          {openDropView && (
            <StyledAutoCompleteList
              absolutePosition={absolutePosition}
              ref={dropViewRef}
              position={dropviewPosition}
              width={dropViewWidth}
              offset={offsetPopover}
            >
              {dropdownHeader}
              {noResultsComponent}
            </StyledAutoCompleteList>
          )}
        </InputContainer>
      </AutoCompleteDiv>
      <StyledIcon>
        <IconBerserkerMail name="search" height={16} width={16} />
      </StyledIcon>
      {selectedValue.length > 0 && (
        <StyledClearIcon
          isCreatingTag={isCategoryComponent || false}
          onClick={(e) => {
            e.stopPropagation();
            clearInput();
          }}
        >
          <IoMdClose color="#777777" size={16} />
        </StyledClearIcon>
      )}

      {isCategoryComponent && (
        <Cancel
          onClick={(e) => {
            e.stopPropagation();
            modalHandler.pushModal(
              <Warning
                title="Are you sure you want to cancel?"
                icon={"./icons/delete.svg"}
                description={`Your new tag "${tagValue}" will not be created.`}
                onOkClicked={() => {
                  onCancel();
                  modalHandler.popModal();
                }}
                onCancelClicked={() => modalHandler.popModal()}
                buttonsName={["No", "Yes"]}
              />
            );
          }}
        >
          Cancel
        </Cancel>
      )}
    </Container>
  );
}
